import { useEffect } from "react";
import { PUBLIC_ASSETS } from "@/constants/assets";
import { EXTERNAL_LINKS } from "@/constants/navigation";

interface DocumentMetadata {
  title: string;
  description: string;
  canonicalUrl: string | null;
  indexable: boolean;
}

export function formatDocumentTitle(pageTitle: string, productName: string) {
  return pageTitle === productName
    ? productName
    : `${pageTitle} | ${productName}`;
}

function getMetaElement(attribute: "name" | "property", key: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existing = document.querySelector<HTMLMetaElement>(selector);
  if (existing) return existing;

  const meta = document.createElement("meta");
  meta.setAttribute(attribute, key);
  document.head.appendChild(meta);
  return meta;
}

function setMetaContent(attribute: "name" | "property", key: string, content: string) {
  getMetaElement(attribute, key).setAttribute("content", content);
}

export function useDocumentMetadata({
  title, description, canonicalUrl, indexable,
}: DocumentMetadata): void {
  useEffect(() => {
    document.title = title;
    setMetaContent("name", "description", description);
    setMetaContent("property", "og:title", title);
    setMetaContent("property", "og:description", description);
    setMetaContent("property", "og:type", "website");
    setMetaContent("name", "robots", indexable ? "index, follow" : "noindex, follow");
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;
      setMetaContent("property", "og:url", canonicalUrl);
    } else {
      canonical?.remove();
      document.querySelector('meta[property="og:url"]')?.remove();
    }
    const imageUrl = new URL(PUBLIC_ASSETS.socialPreview, EXTERNAL_LINKS.website).href;
    setMetaContent("property", "og:image", imageUrl);
    setMetaContent("property", "og:image:width", "1200");
    setMetaContent("property", "og:image:height", "630");
    setMetaContent("name", "twitter:image", imageUrl);
    setMetaContent("name", "twitter:card", "summary_large_image");
    setMetaContent("name", "twitter:title", title);
    setMetaContent("name", "twitter:description", description);
  }, [canonicalUrl, description, indexable, title]);
}
